import { lighten } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TProDirectoryRevision } from '@onesource/schemas';
import {
  useGetCityByIdQuery,
  useGetCountryByIdQuery,
  useGetExpertiseQuery,
  useGetProfessionsQuery,
  useGetStateByIdQuery,
} from '@onesource/services';
import { formatDistanceToNow } from 'date-fns';
import { FC } from 'react';

import { RevisionFieldValue } from './revision-field-value';
import { RevisionImageField } from './revision-image-field';
import { DataShape, FIELD_LABELS } from './revisions-utils';

type Props = {
  revision: TProDirectoryRevision;
  approved: DataShape | null;
};

export const RevisionDetail: FC<Props> = ({ revision, approved }) => {
  const theme = useTheme();
  const stackedCompare = useMediaQuery(theme.breakpoints.down('md'));
  const { data: professions = [] } = useGetProfessionsQuery();
  const professionMap = new Map(professions.filter(p => p.parentId !== null).map(p => [String(p.id), p.name]));
  const { data: expertiseItems = [] } = useGetExpertiseQuery();
  const expertiseMap = new Map(expertiseItems.filter(e => e.parentId !== null).map(e => [String(e.id), e.name]));

  const pendingCountryId = revision.data?.business_country_id as number | undefined;
  const pendingStateId = revision.data?.business_state_id as number | undefined;
  const pendingCityId = revision.data?.business_city_id as number | undefined;
  const approvedCountryId = approved?.business_country_id as number | undefined;
  const approvedStateId = approved?.business_state_id as number | undefined;
  const approvedCityId = approved?.business_city_id as number | undefined;

  const { data: pendingCountry } = useGetCountryByIdQuery(pendingCountryId);
  const { data: approvedCountry } = useGetCountryByIdQuery(approvedCountryId);
  const { data: pendingState } = useGetStateByIdQuery(pendingStateId);
  const { data: approvedState } = useGetStateByIdQuery(approvedStateId);
  const { data: pendingCity } = useGetCityByIdQuery(pendingCityId);
  const { data: approvedCity } = useGetCityByIdQuery(approvedCityId);

  const geoMap = new Map<string, string>();
  if (pendingCountry) {
    geoMap.set(String(pendingCountry.id), pendingCountry.name);
  }
  if (approvedCountry) {
    geoMap.set(String(approvedCountry.id), approvedCountry.name);
  }
  if (pendingState) {
    geoMap.set(String(pendingState.id), pendingState.name);
  }
  if (approvedState) {
    geoMap.set(String(approvedState.id), approvedState.name);
  }
  if (pendingCity) {
    geoMap.set(String(pendingCity.id), pendingCity.name);
  }
  if (approvedCity) {
    geoMap.set(String(approvedCity.id), approvedCity.name);
  }

  return (
    <>
      {revision.status === 'rejected' && revision.rejectionReason && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Rejected:</strong> {revision.rejectionReason}
        </Alert>
      )}

      <Alert severity="warning" sx={{ mb: 2 }}>
        Highlighted fields indicate changes from your approved profile.
      </Alert>

      {!stackedCompare && (
        <Grid container spacing={2} sx={{ mb: 1 }}>
          <Grid item xs={6}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              APPROVED (LIVE)
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" fontWeight={700} color="text.secondary">
              {formatDistanceToNow(new Date(revision.createdAt), { addSuffix: true })}
            </Typography>
          </Grid>
        </Grid>
      )}

      <RevisionImageField
        label="Profile Image"
        approvedVal={approved?.casual_image ?? null}
        pendingVal={revision.data.casual_image ?? null}
        fullScreen={stackedCompare}
        width={120}
        height={120}
      />

      <RevisionImageField
        label="Landscape Image"
        approvedVal={approved?.landscape_image ?? null}
        pendingVal={revision.data.landscape_image ?? null}
        fullScreen={stackedCompare}
        width={200}
        height={120}
      />

      {FIELD_LABELS.map(({ key, label }) => {
        const approvedVal = approved ? approved[key] : null;
        const pendingVal = revision.data[key] ?? null;
        const changed = JSON.stringify(approvedVal) !== JSON.stringify(pendingVal);
        return (
          <Box key={key} sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {label}
            </Typography>
            {stackedCompare ? (
              <>
                <RevisionFieldValue
                  dataKey={key}
                  value={approvedVal}
                  professionMap={professionMap}
                  expertiseMap={expertiseMap}
                  geoMap={geoMap}
                />
                {changed && (
                  <Box
                    sx={{
                      mt: 0.75,
                      bgcolor: theme => lighten(theme.palette.warning.light, 0.9),
                      border: theme => `1px solid ${theme.palette.warning.main}`,
                      borderRadius: 1,
                      px: 1,
                      py: 0.5,
                    }}
                  >
                    <RevisionFieldValue
                      dataKey={key}
                      value={pendingVal}
                      professionMap={professionMap}
                      expertiseMap={expertiseMap}
                      geoMap={geoMap}
                    />
                  </Box>
                )}
              </>
            ) : (
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <RevisionFieldValue
                    dataKey={key}
                    value={approvedVal}
                    professionMap={professionMap}
                    expertiseMap={expertiseMap}
                    geoMap={geoMap}
                  />
                </Grid>
                <Grid item xs={6}>
                  {changed ? (
                    <Box
                      sx={{
                        bgcolor: theme => lighten(theme.palette.warning.light, 0.9),
                        border: theme => `1px solid ${theme.palette.warning.main}`,
                        borderRadius: 1,
                        px: 1,
                        py: 0.5,
                      }}
                    >
                      <RevisionFieldValue
                        dataKey={key}
                        value={pendingVal}
                        professionMap={professionMap}
                        expertiseMap={expertiseMap}
                        geoMap={geoMap}
                      />
                    </Box>
                  ) : (
                    <RevisionFieldValue
                      dataKey={key}
                      value={pendingVal}
                      professionMap={professionMap}
                      expertiseMap={expertiseMap}
                      geoMap={geoMap}
                    />
                  )}
                </Grid>
              </Grid>
            )}
          </Box>
        );
      })}
    </>
  );
};
